
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';

const EMPTY_PROFILE = {
  employeeNumber: '',
  firstName: '',
  lastName: '',
  phone: '',
  hireDate: '',
  terminationDate: '',
  status: 'ACTIVO',
  departmentId: '',
  positionId: '',
  notes: ''
};

const EMPTY_MOVEMENT = {
  type: 'OTRO',
  effectiveDate: '',
  previousValue: '',
  newValue: '',
  reason: ''
};

const EMPTY_ACCOUNT = {
  name: '',
  email: '',
  password: '',
  role: 'Empleado',
  accountStatus: 'ACTIVA'
};

const EmployeeManagement = ({ token, isDarkMode, companyId, initialSelectedUserId = '', canEditExpedientes = false, canManageEmployees = false }) => {
  const [rows, setRows] = useState([]);
  const [catalogs, setCatalogs] = useState({
    departments: [],
    positions: [],
    users: []
  });

  const [selected, setSelected] = useState('');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [movement, setMovement] = useState(EMPTY_MOVEMENT);
  const [movements, setMovements] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [calculationSummary, setCalculationSummary] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const [newDept, setNewDept] = useState({ name: '', code: '' });
  const [newPos, setNewPos] = useState({ name: '', code: '' });

  const [account, setAccount] = useState(EMPTY_ACCOUNT);
  const [newAccount, setNewAccount] = useState(EMPTY_ACCOUNT);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('TODOS');

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const api = '/api/employee-management';

  const colors = {
    card: isDarkMode ? '#111827' : '#fff',
    text: isDarkMode ? '#f1f5f9' : '#1a202c',
    muted: isDarkMode ? '#94a3b8' : '#718096',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    input: isDarkMode ? '#0f172a' : '#fff',
    soft: isDarkMode ? '#172033' : '#f8fafc'
  };

  const input = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 10px',
    borderRadius: 7,
    border: `1px solid ${colors.border}`,
    background: colors.input,
    color: colors.text
  };

  const label = {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: colors.muted,
    marginBottom: 4
  };

  const button = (bg = '#3182ce') => ({
    padding: '9px 13px',
    border: 0,
    borderRadius: 7,
    background: bg,
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer'
  });

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [r, c] = await Promise.all([
        axios.get(api, {
          headers,
          params: companyId ? { companyId } : {}
        }),
        axios.get(`${api}/catalogs`, {
          headers,
          params: companyId ? { companyId } : {}
        })
      ]);

      const loadedRows = Array.isArray(r.data) ? r.data : [];

      // Esta sección administra únicamente empleados.
      setRows(loadedRows.filter(row => ['Empleado', 'Administrador', 'SuperAdministradorEmpresarial'].includes(row.role)));

      setCatalogs(
        c.data || {
          departments: [],
          positions: [],
          users: []
        }
      );
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible cargar la administración de empleados.'
      );
    } finally {
      setLoading(false);
    }
  }, [token, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (initialSelectedUserId && rows.some(r => String(r.id) === String(initialSelectedUserId))) {
      setSelected(String(initialSelectedUserId));
    }
  }, [initialSelectedUserId, rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows.filter(row => {
      const matchesSearch =
        !q ||
        [
          row.name,
          row.email,
          row.id,
          row.profile?.employeeNumber,
          row.department?.name,
          row.position?.name
        ].some(v =>
          String(v ?? '')
            .toLowerCase()
            .includes(q)
        );

      const matchesStatus =
        statusFilter === 'TODOS' ||
        String(row.accountStatus || 'ACTIVA').toUpperCase() === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [rows, search, statusFilter]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter(
        r => String(r.accountStatus || 'ACTIVA').toUpperCase() === 'ACTIVA'
      ).length,
      inactive: rows.filter(
        r => String(r.accountStatus || '').toUpperCase() === 'INACTIVA'
      ).length
    }),
    [rows]
  );

  const selectUser = useCallback(
    async id => {
      setSelected(String(id || ''));

      const row = rows.find(x => String(x.id) === String(id));

      if (!row) {
        setProfile(EMPTY_PROFILE);
        setAccount(EMPTY_ACCOUNT);
        setMovements([]);
        setAttendanceHistory([]);
        return;
      }

      const p = row.profile || {};

      setProfile({
        employeeNumber: p.employeeNumber || '',
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        phone: p.phone || '',
        hireDate: p.hireDate || '',
        terminationDate: p.terminationDate || '',
        status: p.status || 'ACTIVO',
        departmentId: p.departmentId || '',
        positionId: p.positionId || '',
        notes: p.notes || ''
      });

      setAccount({
        name: row.name || '',
        email: row.email || '',
        password: '',
        role: 'Empleado',
        accountStatus: row.accountStatus || 'ACTIVA'
      });

      try {
        const r = await axios.get(`${api}/${id}/movements`, {
          headers,
          params: companyId ? { companyId } : {}
        });

        setMovements(r.data || []);
      } catch (e) {
        setMovements([]);
      }

      setAttendanceLoading(true);
      setCalculationSummary(null);
      try {
        const r = await axios.get(`/api/attendance/calculation/${id}`, { headers });
        const effectiveMarks = Array.isArray(r.data?.marcas) ? r.data.marcas : [];
        setAttendanceHistory(effectiveMarks);
        setCalculationSummary(r.data || null);
      } catch (e) {
        setAttendanceHistory([]);
        setCalculationSummary(null);
      } finally {
        setAttendanceLoading(false);
      }
    },
    [rows, token, companyId]
  );

  const parseAttendanceDate = value => {
    if (!value) return null;
    const raw = String(value);
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const startOfWeek = date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d;
  };

  const addDays = (date, amount) => {
    const d = new Date(date);
    d.setDate(d.getDate() + amount);
    return d;
  };

  const formatDay = date => date.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit' });
  const formatDate = date => date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatMinutes = minutes => { const n = Math.max(0, Math.round(Number(minutes) || 0)); return `${Math.floor(n / 60)} h ${String(n % 60).padStart(2, '0')} min`; };

  const currentWeekKey = startOfWeek(new Date()).toISOString().slice(0, 10);

  const rollingWeeks = useMemo(() => {
    const current = startOfWeek(new Date());
    return [0, 1, 2, 3].map(offset => {
      const start = addDays(current, -offset * 7);
      const end = addDays(start, 6);
      return {
        key: start.toISOString().slice(0, 10),
        start,
        end,
        label: offset === 0 ? 'Semana en curso' : `${offset} semana${offset === 1 ? '' : 's'} anterior${offset === 1 ? '' : 'es'}`
      };
    });
  }, [currentWeekKey]);

  const attendanceByWeek = useMemo(() => {
    const result = new Map();
    rollingWeeks.forEach(week => {
      const days = Array.from({ length: 7 }, (_, index) => ({
        date: addDays(week.start, index),
        records: [],
        minutes: 0
      }));
      result.set(week.key, { ...week, days, totalMinutes: 0 });
    });

    const records = [...attendanceHistory]
      .map(record => ({ ...record, _date: parseAttendanceDate(record.timestamp) }))
      .filter(record => record._date);

    // Los minutos de trabajo NO se vuelven a calcular aquí. El backend ya
    // emparejó las marcas efectivas, aplicó aclaraciones aceptadas y respetó
    // el límite máximo de jornada. Esta vista sólo distribuye el resultado
    // del motor en las cuatro semanas visibles.
    const jornadas = Array.isArray(calculationSummary?.jornadas)
      ? calculationSummary.jornadas
      : [];

    jornadas.forEach(jornada => {
      const fecha = String(jornada.fecha || '').slice(0, 10);
      const minutos = Math.max(0, Math.round(Number(jornada.minutosTrabajados) || 0));
      if (!fecha || !minutos) return;
      const entryDate = new Date(`${fecha}T12:00:00`);
      if (Number.isNaN(entryDate.getTime())) return;

      for (const week of rollingWeeks) {
        const weekStart = new Date(week.start);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = addDays(week.end, 1);
        weekEnd.setHours(0, 0, 0, 0);
        if (entryDate >= weekStart && entryDate < weekEnd) {
          const dayIndex = Math.max(0, Math.min(6, Math.floor((entryDate - weekStart) / 86400000)));
          const bucket = result.get(week.key);
          bucket.days[dayIndex].minutes += minutos;
          bucket.totalMinutes += minutos;
          break;
        }
      }
    });

    // Las marcas efectivas conservan la evidencia de cada día, incluso si la
    // jornada quedó abierta o pendiente de ajuste.
    for (const week of rollingWeeks) {
      const bucket = result.get(week.key);
      records.forEach(record => {
        const d = record._date;
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const weekStart = new Date(week.start.getFullYear(), week.start.getMonth(), week.start.getDate());
        const dayIndex = Math.floor((dayStart - weekStart) / 86400000);
        if (dayIndex >= 0 && dayIndex < 7) bucket.days[dayIndex].records.push(record);
      });
    }

    return result;
  }, [attendanceHistory, calculationSummary, rollingWeeks]);

  const exportWeek = week => {
    if (!selected) return;
    const bucket = attendanceByWeek.get(week.key);
    if (!bucket) return;
    const selectedRow = rows.find(row => String(row.id) === String(selected));
    const lines = [
      ['Empleado', selectedRow?.name || '', 'ID', selected],
      ['Semana', formatDate(week.start), 'a', formatDate(week.end)],
      [],
      ['Fecha', 'Marcas', 'Horas realizadas']
    ];
    bucket.days.forEach(day => {
      const marks = day.records
        .sort((a, b) => a._date - b._date)
        .map(record => `${record.type} ${record._date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`)
        .join(' | ');
      lines.push([formatDate(day.date), marks || 'Sin marca', formatMinutes(day.minutes)]);
    });
    lines.push([], ['Total semanal', '', formatMinutes(bucket.totalMinutes)]);

    const csv = lines.map(row => row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([`\\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `asistencia_${selectedRow?.name || selected}_${week.key}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const createAccount = async e => {
    e.preventDefault();

    if (
      !newAccount.name.trim() ||
      !newAccount.email.trim() ||
      !newAccount.password
    ) {
      return setMessage(
        'Nombre, correo y contraseña son obligatorios.'
      );
    }

    if (newAccount.password.length < 8) {
      return setMessage(
        'La contraseña debe tener al menos 8 caracteres.'
      );
    }

    setCreating(true);

    try {
      const r = await axios.post(
        `${api}/account`,
        {
          name: newAccount.name,
          email: newAccount.email,
          password: newAccount.password,
          role: 'Empleado',
          accountStatus: 'ACTIVA',
          companyId: companyId || undefined
        },
        { headers }
      );

      setMessage(
        r.data?.message || 'Empleado creado correctamente.'
      );

      setNewAccount(EMPTY_ACCOUNT);

      await load();

      if (r.data?.data?.id) {
        setTimeout(() => selectUser(r.data.data.id), 0);
      }
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible crear la cuenta del empleado.'
      );
    } finally {
      setCreating(false);
    }
  };

  const saveAccount = async e => {
    e.preventDefault();

    if (!selected) {
      return setMessage('Selecciona un empleado.');
    }

    setSavingAccount(true);

    try {
      const payload = {
        name: account.name,
        email: account.email,
        role: 'Empleado',
        accountStatus: account.accountStatus,
        companyId: companyId || undefined
      };

      if (account.password) {
        payload.password = account.password;
      }

      await axios.put(
        `${api}/${selected}/account`,
        payload,
        { headers }
      );

      setMessage('Cuenta del empleado actualizada correctamente.');

      setAccount(prev => ({
        ...prev,
        password: '',
        role: 'Empleado'
      }));

      await load();
      await selectUser(selected);
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible actualizar la cuenta del empleado.'
      );
    } finally {
      setSavingAccount(false);
    }
  };

  const save = async e => {
    e.preventDefault();

    if (!selected) {
      return setMessage('Selecciona un empleado.');
    }

    setSaving(true);

    try {
      await axios.put(
        `${api}/${selected}/profile`,
        {
          ...profile,
          companyId: companyId || undefined,
          departmentId: profile.departmentId || null,
          positionId: profile.positionId || null
        },
        { headers }
      );

      setMessage(
        'Expediente laboral guardado correctamente.'
      );

      await load();
      await selectUser(selected);
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible guardar el expediente.'
      );
    } finally {
      setSaving(false);
    }
  };

  const addCatalog = async kind => {
    const data = kind === 'department' ? newDept : newPos;

    if (!data.name.trim()) {
      return setMessage('Indica el nombre del catálogo.');
    }

    try {
      await axios.post(
        `${api}/${kind === 'department' ? 'departments' : 'positions'}`,
        {
          ...data,
          companyId: companyId || undefined
        },
        { headers }
      );

      setMessage(
        kind === 'department'
          ? 'Departamento creado.'
          : 'Puesto creado.'
      );

      if (kind === 'department') {
        setNewDept({ name: '', code: '' });
      } else {
        setNewPos({ name: '', code: '' });
      }

      await load();
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible crear el catálogo.'
      );
    }
  };

  const addMovement = async e => {
    e.preventDefault();

    if (!selected) {
      return setMessage('Selecciona un empleado.');
    }

    if (!movement.effectiveDate) {
      return setMessage('Indica la fecha efectiva.');
    }

    try {
      await axios.post(
        `${api}/${selected}/movements`,
        {
          ...movement,
          companyId: companyId || undefined
        },
        { headers }
      );

      setMessage('Movimiento laboral registrado.');
      setMovement(EMPTY_MOVEMENT);

      await selectUser(selected);
      await load();
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible registrar el movimiento.'
      );
    }
  };

  const toggleAccount = async row => {
    const next =
      String(row.accountStatus || 'ACTIVA').toUpperCase() === 'ACTIVA'
        ? 'INACTIVA'
        : 'ACTIVA';

    if (
      !window.confirm(
        `${next === 'INACTIVA' ? '¿Desactivar' : '¿Activar'} la cuenta de ${row.name}?`
      )
    ) {
      return;
    }

    try {
      await axios.put(
        `${api}/${row.id}/account`,
        {
          accountStatus: next,
          role: 'Empleado',
          companyId: companyId || undefined
        },
        { headers }
      );

      setMessage(
        `Cuenta ${next === 'ACTIVA' ? 'activada' : 'desactivada'} correctamente.`
      );

      await load();

      if (String(selected) === String(row.id)) {
        await selectUser(row.id);
      }
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
        'No fue posible cambiar el estado de la cuenta.'
      );
    }
  };

  return (
    <section
      style={{
        background: colors.card,
        color: colors.text,
        padding: 22,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        marginBottom: 30
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 15,
          flexWrap: 'wrap',
          marginBottom: 18
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>👥 Capital Humano · Expedientes</h2>

          <p
            style={{
              margin: '5px 0 0',
              color: colors.muted,
              fontSize: 13
            }}
          >
            Expedientes laborales de empleados y administradores. Las cuentas, contraseñas y permisos administrativos se mantienen separados.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          style={button('#319795')}
        >
          {loading ? '⏳ Cargando…' : '🔄 Actualizar'}
        </button>
      </div>

      {message && (
        <div
          style={{
            padding: 10,
            marginBottom: 15,
            border: `1px solid ${colors.border}`,
            borderRadius: 7,
            background: colors.soft,
            fontSize: 13
          }}
        >
          {message}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(145px,1fr))',
          gap: 10,
          marginBottom: 18
        }}
      >
        {[
          ['👥', 'Total', stats.total],
          ['🟢', 'Activas', stats.active],
          ['⚪', 'Inactivas', stats.inactive]
        ].map(x => (
          <div
            key={x[1]}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: 9,
              padding: 12,
              background: colors.soft
            }}
          >
            <div
              style={{
                color: colors.muted,
                fontSize: 11
              }}
            >
              {x[0]} {x[1]}
            </div>

            <strong style={{ fontSize: 22 }}>
              {x[2]}
            </strong>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(280px,1.1fr) minmax(300px,1.9fr)',
          gap: 18,
          alignItems: 'start'
        }}
      >
        <div
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: 14,
            background: colors.soft
          }}
        >
          <h3 style={{ margin: '0 0 12px' }}>
            ➕ Nuevo empleado
          </h3>

          <form
            onSubmit={createAccount}
            style={{
              display: 'grid',
              gap: 10
            }}
          >
            <div>
              <label style={label}>Nombre completo</label>

              <input
                style={input}
                value={newAccount.name}
                onChange={e =>
                  setNewAccount({
                    ...newAccount,
                    name: e.target.value
                  })
                }
                placeholder="Ej. Carlos Mendoza"
                required
              />
            </div>

            <div>
              <label style={label}>Correo</label>

              <input
                type="email"
                style={input}
                value={newAccount.email}
                onChange={e =>
                  setNewAccount({
                    ...newAccount,
                    email: e.target.value
                  })
                }
                required
              />
            </div>

            <div>
              <label style={label}>Contraseña inicial</label>

              <input
                type="password"
                style={input}
                value={newAccount.password}
                onChange={e =>
                  setNewAccount({
                    ...newAccount,
                    password: e.target.value
                  })
                }
                placeholder="Mínimo 8 caracteres"
                required
              />
            </div>

            <button
              type="submit"
              disabled={creating}
              style={button()}
            >
              {creating
                ? '⏳ Creando…'
                : '➕ Crear empleado'}
            </button>
          </form>
        </div>

        <div
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: 14
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'minmax(180px,2fr) minmax(120px,1fr)',
              gap: 8,
              marginBottom: 10
            }}
          >
            <input
              style={input}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="🔎 Buscar nombre, correo, ID, departamento…"
            />

            <select
              style={input}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="ACTIVA">Activas</option>
              <option value="INACTIVA">Inactivas</option>
            </select>
          </div>

          <div
            style={{
              fontSize: 11,
              color: colors.muted,
              marginBottom: 8
            }}
          >
            {filteredRows.length} resultado(s) ·{' '}
            {loading
              ? 'cargando…'
              : 'directorio actualizado'}
          </div>

          <div
            style={{
              display: 'grid',
              gap: 7,
              maxHeight: 410,
              overflowY: 'auto'
            }}
          >
            {filteredRows.map(row => {
              const active =
                String(
                  row.accountStatus || 'ACTIVA'
                ).toUpperCase() === 'ACTIVA';

              return (
                <div
                  key={row.id}
                  style={{
                    padding: 11,
                    borderRadius: 8,
                    border: `1px solid ${
                      String(selected) === String(row.id)
                        ? '#3182ce'
                        : colors.border
                    }`,
                    background:
                      String(selected) === String(row.id)
                        ? isDarkMode
                          ? '#1e3a5f'
                          : '#ebf4ff'
                        : colors.card
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      alignItems: 'center'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => selectUser(row.id)}
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        border: 0,
                        background: 'transparent',
                        color: colors.text,
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      <strong>{row.name}</strong>

                      <div
                        style={{
                          color: colors.muted,
                          fontSize: 11,
                          marginTop: 2
                        }}
                      >
                        ID {row.id} · {row.role === 'Empleado' ? 'Empleado' : row.role} ·{' '}
                        {active ? 'ACTIVA' : 'INACTIVA'}
                      </div>

                      <div
                        style={{
                          color: colors.muted,
                          fontSize: 11
                        }}
                      >
                        {row.email}
                      </div>

                      <div
                        style={{
                          color: colors.muted,
                          fontSize: 10
                        }}
                      >
                        {row.department?.name ||
                          'Sin departamento'}{' '}
                        ·{' '}
                        {row.position?.name ||
                          'Sin puesto'}
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleAccount(row)}
                      style={button(
                        active ? '#718096' : '#38a169'
                      )}
                    >
                      {active
                        ? 'Desactivar'
                        : 'Activar'}
                    </button>
                  </div>
                </div>
              );
            })}

            {!filteredRows.length && (
              <div
                style={{
                  padding: 20,
                  textAlign: 'center',
                  color: colors.muted
                }}
              >
                No hay personas que coincidan con el filtro.
              </div>
            )}
          </div>
        </div>
      </div>

      {selected && (
        <div style={{ marginTop: 18, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: 0 }}>📅 Asistencia de las últimas 4 semanas</h3>
              <div style={{ marginTop: 4, color: colors.muted, fontSize: 12 }}>Semana en curso + las 3 anteriores. La vista avanza automáticamente cada semana.</div>
            </div>
            {attendanceLoading && <span style={{ color: colors.muted, fontSize: 12 }}>Cargando marcas…</span>}
          </div>

          <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
            {rollingWeeks.map(week => {
              const bucket = attendanceByWeek.get(week.key);
              return (
                <div key={week.key} style={{ border: `1px solid ${colors.border}`, borderRadius: 9, padding: 11 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 9 }}>
                    <div>
                      <strong>{week.label}</strong>
                      <span style={{ marginLeft: 8, color: colors.muted, fontSize: 12 }}>{formatDate(week.start)} – {formatDate(week.end)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong>{formatMinutes(bucket?.totalMinutes || 0)}</strong>
                      <button type="button" onClick={() => exportWeek(week)} style={button('#4a5568')}>⬇️ Exportar</button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(72px,1fr))', gap: 6, overflowX: 'auto' }}>
                    {(bucket?.days || []).map(day => {
                      const attended = day.records.length > 0;
                      const marks = day.records.slice().sort((a, b) => a._date - b._date);
                      return (
                        <div key={day.date.toISOString()} style={{ minWidth: 72, minHeight: 82, border: `1px solid ${colors.border}`, borderRadius: 7, padding: 7, background: attended ? (isDarkMode ? '#123524' : '#f0fff4') : colors.soft }}>
                          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'capitalize' }}>{formatDay(day.date)}</div>
                          <div style={{ fontSize: 18, marginTop: 3 }}>{attended ? '🟢' : '—'}</div>
                          <div style={{ fontSize: 10, color: colors.muted }}>{attended ? marks.map((mark, index) => <div key={`${mark.id}-${index}`}>{mark.type === 'Entrada' ? 'E' : 'S'} {mark._date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</div>) : 'Sin marca'}</div>
                          {day.minutes > 0 && <div style={{ marginTop: 4, fontSize: 10, fontWeight: 700 }}>{formatMinutes(day.minutes)}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected && rows.find(r => String(r.id) === String(selected))?.role === 'Empleado' && (
        <>
          <hr
            style={{
              border: 0,
              borderTop: `1px solid ${colors.border}`,
              margin: '24px 0'
            }}
          />

          <div
            style={{
              padding: 14,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              background: colors.soft,
              marginBottom: 18
            }}
          >
            <h3 style={{ margin: '0 0 12px' }}>
              🔐 Seguridad de la cuenta
            </h3>

            <form
              onSubmit={saveAccount}
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit,minmax(180px,1fr))',
                gap: 10,
                alignItems: 'end'
              }}
            >
              <div>
                <label style={label}>
                  Nombre de acceso
                </label>

                <input
                  style={input}
                  value={account.name}
                  onChange={e =>
                    setAccount({
                      ...account,
                      name: e.target.value
                    })
                  }
                  required
                />
              </div>

              <div>
                <label style={label}>Correo</label>

                <input
                  type="email"
                  style={input}
                  value={account.email}
                  onChange={e =>
                    setAccount({
                      ...account,
                      email: e.target.value
                    })
                  }
                  required
                />
              </div>

              <div>
                <label style={label}>
                  Estado de cuenta
                </label>

                <select
                  style={input}
                  value={account.accountStatus}
                  onChange={e =>
                    setAccount({
                      ...account,
                      accountStatus: e.target.value
                    })
                  }
                >
                  <option value="ACTIVA">
                    ACTIVA
                  </option>

                  <option value="INACTIVA">
                    INACTIVA
                  </option>
                </select>
              </div>

              <div>
                <label style={label}>
                  Nueva contraseña
                </label>

                <input
                  type="password"
                  style={input}
                  value={account.password}
                  onChange={e =>
                    setAccount({
                      ...account,
                      password: e.target.value
                    })
                  }
                  placeholder="Opcional · mínimo 8"
                />
              </div>

              <button
                type="submit"
                disabled={savingAccount}
                style={button('#805ad5')}
              >
                {savingAccount
                  ? '⏳ Guardando…'
                  : '💾 Guardar cuenta'}
              </button>
            </form>
          </div>

          <div
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: 14
            }}
          >
            <h3 style={{ margin: '0 0 12px' }}>
              📁 Expediente laboral
            </h3>

            <form
              onSubmit={save}
              style={{
                display: 'grid',
                gap: 12
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit,minmax(160px,1fr))',
                  gap: 10
                }}
              >
                <div>
                  <label style={label}>
                    No. empleado
                  </label>

                  <input
                    style={input}
                    value={profile.employeeNumber}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        employeeNumber: e.target.value
                      })
                    }
                  />
                </div>

                <div>
                  <label style={label}>Nombre</label>

                  <input
                    style={input}
                    value={profile.firstName}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        firstName: e.target.value
                      })
                    }
                  />
                </div>

                <div>
                  <label style={label}>
                    Apellidos
                  </label>

                  <input
                    style={input}
                    value={profile.lastName}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        lastName: e.target.value
                      })
                    }
                  />
                </div>

                <div>
                  <label style={label}>Teléfono</label>

                  <input
                    style={input}
                    value={profile.phone}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        phone: e.target.value
                      })
                    }
                  />
                </div>

                <div>
                  <label style={label}>
                    Fecha de ingreso
                  </label>

                  <input
                    type="date"
                    style={input}
                    value={profile.hireDate}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        hireDate: e.target.value
                      })
                    }
                  />
                </div>

                <div>
                  <label style={label}>
                    Fecha de baja
                  </label>

                  <input
                    type="date"
                    style={input}
                    value={profile.terminationDate}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        terminationDate: e.target.value
                      })
                    }
                  />
                </div>

                <div>
                  <label style={label}>
                    Estado laboral
                  </label>

                  <select
                    style={input}
                    value={profile.status}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        status: e.target.value
                      })
                    }
                  >
                    {[
                      'ACTIVO',
                      'INACTIVO',
                      'BAJA',
                      'SUSPENDIDO',
                      'LICENCIA'
                    ].map(x => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={label}>
                    Departamento
                  </label>

                  <select
                    style={input}
                    value={profile.departmentId || ''}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        departmentId: e.target.value
                      })
                    }
                  >
                    <option value="">
                      Sin departamento
                    </option>

                    {catalogs.departments
                      .filter(
                        x => x.status === 'ACTIVO'
                      )
                      .map(x => (
                        <option
                          key={x.id}
                          value={x.id}
                        >
                          {x.name}
                          {x.code
                            ? ` · ${x.code}`
                            : ''}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label style={label}>
                    Puesto
                  </label>

                  <select
                    style={input}
                    value={profile.positionId || ''}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        positionId: e.target.value
                      })
                    }
                  >
                    <option value="">
                      Sin puesto
                    </option>

                    {catalogs.positions
                      .filter(
                        x => x.status === 'ACTIVO'
                      )
                      .map(x => (
                        <option
                          key={x.id}
                          value={x.id}
                        >
                          {x.name}
                          {x.code
                            ? ` · ${x.code}`
                            : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={label}>Notas</label>

                <textarea
                  style={{
                    ...input,
                    minHeight: 75,
                    resize: 'vertical'
                  }}
                  value={profile.notes}
                  onChange={e =>
                    setProfile({
                      ...profile,
                      notes: e.target.value
                    })
                  }
                />
              </div>

              <button
                type="submit"
                disabled={saving || !canEditExpedientes}
                title={!canEditExpedientes ? 'Tu alcance solo permite consultar expedientes.' : undefined}
                style={button()}
              >
                {saving
                  ? '⏳ Guardando…'
                  : '💾 Guardar expediente'}
              </button>
            </form>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit,minmax(260px,1fr))',
              gap: 18,
              marginTop: 18
            }}
          >
            <div
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: 14
              }}
            >
              <h3 style={{ marginTop: 0 }}>
                🏷️ Departamentos
              </h3>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    '1fr 100px auto',
                  gap: 7
                }}
              >
                <input
                  style={input}
                  placeholder="Nombre"
                  value={newDept.name}
                  onChange={e =>
                    setNewDept({
                      ...newDept,
                      name: e.target.value
                    })
                  }
                />

                <input
                  style={input}
                  placeholder="Código"
                  value={newDept.code}
                  onChange={e =>
                    setNewDept({
                      ...newDept,
                      code: e.target.value
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() =>
                    addCatalog('department')
                  }
                  style={button('#319795')}
                >
                  Añadir
                </button>
              </div>

              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: colors.muted
                }}
              >
                {catalogs.departments
                  .map(
                    x =>
                      `${x.name}${
                        x.code ? ` · ${x.code}` : ''
                      } · ${x.status}`
                  )
                  .join(' | ') ||
                  'Sin departamentos'}
              </div>
            </div>

            <div
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: 14
              }}
            >
              <h3 style={{ marginTop: 0 }}>
                💼 Puestos
              </h3>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    '1fr 100px auto',
                  gap: 7
                }}
              >
                <input
                  style={input}
                  placeholder="Nombre"
                  value={newPos.name}
                  onChange={e =>
                    setNewPos({
                      ...newPos,
                      name: e.target.value
                    })
                  }
                />

                <input
                  style={input}
                  placeholder="Código"
                  value={newPos.code}
                  onChange={e =>
                    setNewPos({
                      ...newPos,
                      code: e.target.value
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() =>
                    addCatalog('position')
                  }
                  style={button('#319795')}
                >
                  Añadir
                </button>
              </div>

              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: colors.muted
                }}
              >
                {catalogs.positions
                  .map(
                    x =>
                      `${x.name}${
                        x.code ? ` · ${x.code}` : ''
                      } · ${x.status}`
                  )
                  .join(' | ') ||
                  'Sin puestos'}
              </div>
            </div>
          </div>

          <div
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: 14,
              marginTop: 18
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              📜 Movimientos laborales
            </h3>

            <form
              onSubmit={addMovement}
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit,minmax(150px,1fr))',
                gap: 10,
                alignItems: 'end'
              }}
            >
              <div>
                <label style={label}>Tipo</label>

                <select
                  style={input}
                  value={movement.type}
                  onChange={e =>
                    setMovement({
                      ...movement,
                      type: e.target.value
                    })
                  }
                >
                  {[
                    'ALTA',
                    'CAMBIO_DEPARTAMENTO',
                    'CAMBIO_DE_PUESTO',
                    'CAMBIO_ESTADO',
                    'REINGRESO',
                    'BAJA',
                    'OTRO'
                  ].map(x => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={label}>
                  Fecha efectiva
                </label>

                <input
                  type="date"
                  style={input}
                  value={movement.effectiveDate}
                  onChange={e =>
                    setMovement({
                      ...movement,
                      effectiveDate: e.target.value
                    })
                  }
                />
              </div>

              <div>
                <label style={label}>
                  Valor anterior
                </label>

                <input
                  style={input}
                  value={movement.previousValue}
                  onChange={e =>
                    setMovement({
                      ...movement,
                      previousValue: e.target.value
                    })
                  }
                />
              </div>

              <div>
                <label style={label}>
                  Nuevo valor
                </label>

                <input
                  style={input}
                  value={movement.newValue}
                  onChange={e =>
                    setMovement({
                      ...movement,
                      newValue: e.target.value
                    })
                  }
                />
              </div>

              <div>
                <label style={label}>Motivo</label>

                <input
                  style={input}
                  value={movement.reason}
                  onChange={e =>
                    setMovement({
                      ...movement,
                      reason: e.target.value
                    })
                  }
                />
              </div>

              <button
                type="submit"
                style={button('#805ad5')}
              >
                Registrar movimiento
              </button>
            </form>

            <div
              style={{
                marginTop: 12,
                display: 'grid',
                gap: 7
              }}
            >
              {movements.map(m => (
                <div
                  key={m.id}
                  style={{
                    padding: 10,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 7,
                    fontSize: 12
                  }}
                >
                  <strong>
                    {m.effectiveDate} · {m.type}
                  </strong>{' '}
                  · {m.previousValue || '—'} →{' '}
                  {m.newValue || '—'}
                  {m.reason
                    ? ` · ${m.reason}`
                    : ''}
                </div>
              ))}

              {!movements.length && (
                <div
                  style={{
                    color: colors.muted,
                    fontSize: 12
                  }}
                >
                  Sin movimientos registrados.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default EmployeeManagement;
